/**
 * Overview — the dashboard home page.
 *
 * Seven stat cards on top (MRR / customers / founding spots / total
 * employees / API spend today / active users today / newsletter
 * subscribers), two trend charts (signups + MRR over last 30d), and a
 * recent-activity feed.
 *
 * Two parallel fetches hydrate everything:
 *   - ``GET /admin/overview`` — stats + 30d series + recent activity
 *   - ``GET /admin/newsletter`` — subscriber count (separate endpoint
 *     so the existing overview payload doesn't need a schema change)
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bot,
  Building2,
  Loader2,
  Mail,
  Sparkles,
  TrendingUp,
  UserPlus,
  Users,
} from "lucide-react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  getNewsletter,
  getOverview,
  NewsletterResponse,
  OverviewResponse,
} from "@/api/admin";
import { fmtNum, fmtRelative, fmtUsd } from "@/lib/utils";

export function OverviewPage() {
  const [data, setData] = useState<OverviewResponse | null>(null);
  const [newsletter, setNewsletter] = useState<NewsletterResponse | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Fetch both in parallel — the page renders as soon as the
    // overview lands; the newsletter tile shows "—" until its own
    // fetch completes, which keeps the founder's first paint fast
    // even when one of the two endpoints is slow.
    getOverview()
      .then((d) => !cancelled && setData(d))
      .catch((e) =>
        !cancelled && setError(e?.message || "Failed to load overview"),
      );
    // Newsletter failure is non-blocking — we'd rather show the rest
    // of the dashboard with a "—" subscriber count than fail the
    // whole page if /admin/newsletter is down.
    getNewsletter()
      .then((d) => !cancelled && setNewsletter(d))
      .catch(() => {
        /* swallow — newsletter tile renders "—" */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <div className="p-8 text-sm text-danger">
        <p>Failed to load overview: {error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-8 flex items-center gap-2 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading…
      </div>
    );
  }

  const s = data.stats;

  return (
    <div className="p-6 space-y-6">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">Overview</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Live snapshot of the Lenavio platform. Cross-tenant data.
        </p>
      </header>

      {/* ── Stat cards (7) ────────────────────────────────── */}
      <section className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard
          label="Total MRR"
          value={fmtUsd(s.total_mrr_usd)}
          icon={<TrendingUp className="h-4 w-4" />}
          tone="accent"
        />
        <StatCard
          label="Active customers"
          value={fmtNum(s.active_customers)}
          icon={<Building2 className="h-4 w-4" />}
        />
        <StatCard
          label="Founding spots left"
          value={fmtNum(s.founding_spots_remaining)}
          icon={<Sparkles className="h-4 w-4" />}
          tone={
            s.founding_spots_remaining <= 5
              ? "warning"
              : s.founding_spots_remaining === 0
                ? "danger"
                : "default"
          }
        />
        <StatCard
          label="Employees (all orgs)"
          value={fmtNum(s.total_employees)}
          icon={<Users className="h-4 w-4" />}
        />
        <StatCard
          label="API spend today"
          value={fmtUsd(s.api_spend_today_usd)}
          icon={<Bot className="h-4 w-4" />}
          tone={
            s.api_spend_today_usd > 50
              ? "warning"
              : s.api_spend_today_usd > 200
                ? "danger"
                : "default"
          }
        />
        <StatCard
          label="Active users today"
          value={fmtNum(s.active_users_today)}
          icon={<UserPlus className="h-4 w-4" />}
        />
        <StatCard
          label="Newsletter subs"
          // Render "—" while the parallel newsletter fetch is in
          // flight, so the row doesn't reflow when it lands.
          value={newsletter ? fmtNum(newsletter.total_active) : "—"}
          icon={<Mail className="h-4 w-4" />}
        />
      </section>

      {/* ── Trend charts ─────────────────────────────────── */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="MRR — last 30 days">
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={data.mrr_30d}>
              <defs>
                <linearGradient id="mrrGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="5%"
                    stopColor="#6366F1"
                    stopOpacity={0.4}
                  />
                  <stop
                    offset="95%"
                    stopColor="#6366F1"
                    stopOpacity={0}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis
                dataKey="date"
                tickFormatter={(d) => d.slice(5)}
                fontSize={11}
                stroke="#94A3B8"
              />
              <YAxis fontSize={11} stroke="#94A3B8" />
              <Tooltip
                formatter={(v: number) => fmtUsd(v)}
                labelClassName="text-xs"
              />
              <Area
                type="monotone"
                dataKey="mrr_usd"
                stroke="#6366F1"
                strokeWidth={2}
                fill="url(#mrrGradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="New signups — last 30 days">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.signups_30d}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis
                dataKey="date"
                tickFormatter={(d) => d.slice(5)}
                fontSize={11}
                stroke="#94A3B8"
              />
              <YAxis fontSize={11} stroke="#94A3B8" allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" fill="#10B981" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </section>

      {/* ── Recent activity feed ───────────────────────────── */}
      <section>
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <header className="px-4 py-3 border-b border-slate-200">
            <h2 className="text-sm font-semibold text-slate-900">
              Recent activity
            </h2>
          </header>
          <ul className="divide-y divide-slate-100">
            {data.recent_activity.length === 0 && (
              <li className="px-4 py-6 text-sm text-slate-500 text-center">
                No activity yet.
              </li>
            )}
            {data.recent_activity.map((event, i) => (
              <li
                key={`${event.org_id}-${event.at}-${i}`}
                className="px-4 py-3 flex items-center justify-between gap-3 hover:bg-slate-50"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <ActivityIcon type={event.type} />
                  <Link
                    to={`/customers/${event.org_id}`}
                    className="text-sm text-slate-900 hover:text-accent truncate"
                  >
                    {event.label}
                  </Link>
                </div>
                <span className="text-xs text-slate-500 shrink-0">
                  {fmtRelative(event.at)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}

/** A single numeric stat tile. Tone drives the accent color so the
 *  founder's eye lands on the warning / danger cards first. */
function StatCard({
  label,
  value,
  icon,
  tone = "default",
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  tone?: "default" | "accent" | "warning" | "danger";
}) {
  const toneClass = {
    default: "text-slate-500",
    accent: "text-accent",
    warning: "text-warning",
    danger: "text-danger",
  }[tone];
  return (
    <div className="bg-white rounded-lg border border-slate-200 px-4 py-3.5">
      <div className="flex items-center justify-between text-xs">
        <span className="uppercase tracking-wide text-slate-500">{label}</span>
        <span className={toneClass}>{icon}</span>
      </div>
      <div className="mt-1.5 text-2xl font-semibold tabular-nums text-slate-900">
        {value}
      </div>
    </div>
  );
}

function ChartCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4">
      <h2 className="text-sm font-semibold text-slate-900 mb-3">{title}</h2>
      {children}
    </div>
  );
}

function ActivityIcon({ type }: { type: string }) {
  if (type === "signup") {
    return <UserPlus className="h-4 w-4 text-success shrink-0" />;
  }
  if (type === "credit_purchase") {
    return <Sparkles className="h-4 w-4 text-accent shrink-0" />;
  }
  if (type === "subscription") {
    return <TrendingUp className="h-4 w-4 text-accent shrink-0" />;
  }
  return <Building2 className="h-4 w-4 text-slate-400 shrink-0" />;
}
